import { createContext, useContext, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useIsRestoring, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { READ_ONLY_SPECIALTIES } from "@shared/page-access";
import { clearUserQueries, confirmLogout, readIdentitySession, readLogoutSession, logoutReconciliationInterval, recheckIdentity } from "./logout";
import { toast } from "@/hooks/use-toast";
import { setShopFloorReplayHold } from "./shop-floor-offline";
import { sessionChangedDuringSwitch, switchReachedExpected, type AuthUser, type SwitchExpectation } from "./auth-user";
import { isIdentityLoading } from "./identity-loading";

export class IdentityRecoveryError extends Error {
  name = "IdentityRecoveryError";
}

interface UserIdentity {
  user: AuthUser | null;
  displayName: string;
  email: string;
  isSet: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  role: string | null;
  isAdmin: boolean;
  isManager: boolean;
  specialties: string[];
  managedSpecialties: string[];
  allSpecialties: string[];
  isRoleLoaded: boolean;
  isReadOnly: boolean;
  isSuperAdmin: boolean;
  isImpersonating: boolean;
  originalDisplayName: string;
  hasSpecialty: (s: string) => boolean;
  hasAnySpecialty: (ss: string[]) => boolean;
  logout: () => Promise<void>;
  isLoggingOut: boolean;
  stopImpersonating: () => Promise<void>;
  startImpersonating: (userId: string) => Promise<void>;
  isIdentityUnconfirmed: boolean;
  isCheckingIdentity: boolean;
  identityRecoveryError: string | null;
  retryIdentity: () => Promise<void>;
}

const noop = () => false;

const UserContext = createContext<UserIdentity>({
  user: null,
  displayName: "",
  email: "",
  isSet: false,
  isAuthenticated: false,
  isLoading: true,
  role: null,
  isAdmin: false,
  isManager: false,
  specialties: [],
  managedSpecialties: [],
  allSpecialties: [],
  isRoleLoaded: false,
  isReadOnly: false,
  isSuperAdmin: false,
  isImpersonating: false,
  originalDisplayName: "",
  hasSpecialty: noop,
  hasAnySpecialty: noop,
  logout: async () => {},
  isLoggingOut: false,
  stopImpersonating: async () => {},
  startImpersonating: async () => {},
  isIdentityUnconfirmed: false,
  isCheckingIdentity: false,
  identityRecoveryError: null,
  retryIdentity: async () => {},
});

export function UserProvider({ children }: { children: ReactNode }) {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutUncertain, setLogoutUncertain] = useState(false);
  const logoutPending = useRef(false);
  const logoutUserId = useRef<string | null>(null);
  const reconciliationStartedAt = useRef<number | null>(null);
  const logoutError = useRef<ReturnType<typeof toast> | null>(null);
  // Keep the private subtree's identity stable while it is concealed, so an
  // unsuccessful auth refetch cannot unmount its forms and discard drafts.
  const [heldIdentity, setHeldIdentity] = useState<{ user: AuthUser | null } | null>(null);
  const heldIdentityRef = useRef<typeof heldIdentity>(null);
  const identityPending = useRef(false);
  const [isCheckingIdentity, setIsCheckingIdentity] = useState(false);
  const [identityRecoveryError, setIdentityRecoveryError] = useState<string | null>(null);
  // HUB-614: the identity query does not start until the saved shop-floor cache is restored.
  const cacheRestoring = useIsRestoring();
  const { data: user, isLoading, isSuccess } = useQuery<AuthUser | null>({
    queryKey: ["/api/auth/me"],
    queryFn: async context => {
      if (!logoutUncertain) return readIdentitySession(queryClient, context);
      const session = await readLogoutSession(context.signal);
      if (session.ended) {
        await clearUserQueries(queryClient);
        return null;
      }
      const currentUser = queryClient.getQueryData<AuthUser | null>(["/api/auth/me"]);
      if (session.user) {
        // Another tab can replace the cookie with a different valid account.
        // Clear the departing account's queries before showing the current server identity.
        if (session.user.id !== currentUser?.id) await clearUserQueries(queryClient);
        return session.user;
      }
      // A malformed body or failed check proves neither logout nor a new identity.
      if (!currentUser) throw new Error("Session state is still unconfirmed");
      return currentUser;
    },
    staleTime: logoutUncertain ? 0 : 5 * 60 * 1000,
    // A timed-out POST may still be destroying the server session after its
    // first reconciliation returned 200. Bound polling so failed logout cannot
    // keep polling indefinitely; focus/retry still recheck.
    refetchInterval: () => logoutUncertain ? logoutReconciliationInterval(reconciliationStartedAt.current) : false,
    refetchIntervalInBackground: logoutUncertain,
    retry: false,
    enabled: !isLoggingOut && !heldIdentity,
  });

  useEffect(() => {
    const accountChanged = logoutUncertain && user && user.id !== logoutUserId.current;
    if (user !== null && !accountChanged) return;
    reconciliationStartedAt.current = null;
    setLogoutUncertain(false);
    logoutError.current?.dismiss();
    logoutError.current = null;
  }, [user, logoutUncertain]);

  useEffect(() => {
    setShopFloorReplayHold(queryClient, Boolean(heldIdentity) || isCheckingIdentity);
    return () => setShopFloorReplayHold(queryClient, false);
  }, [heldIdentity, isCheckingIdentity]);

  const logout = async () => {
    // Synchronous guard also covers two clicks before React disables the controls.
    if (logoutPending.current || identityPending.current) return;
    logoutPending.current = true;
    logoutUserId.current = user?.id ?? null;
    setIsLoggingOut(true);
    logoutError.current?.dismiss();
    logoutError.current = null;
    setIdentityRecoveryError(null);
    try {
      await confirmLogout(queryClient);
      reconciliationStartedAt.current = null;
      setLogoutUncertain(false);
      heldIdentityRef.current = null;
      setHeldIdentity(null);
    } catch {
      reconciliationStartedAt.current = Date.now();
      setLogoutUncertain(true);
      if (heldIdentityRef.current) setIdentityRecoveryError("Sign out failed. Your account is still unconfirmed.");
      logoutError.current = toast({
        title: "Sign out failed",
        description: "You may still be signed in. Try again before leaving this device.",
        variant: "destructive",
        severity: "error",
      });
    } finally {
      logoutPending.current = false;
      setIsLoggingOut(false);
    }
  };

  const confirmIdentity = async () => {
    const confirmed = await recheckIdentity(queryClient);
    heldIdentityRef.current = null;
    setHeldIdentity(null);
    return confirmed;
  };

  const retryIdentity = async () => {
    if (!heldIdentityRef.current || identityPending.current || logoutPending.current) return;
    identityPending.current = true;
    setIsCheckingIdentity(true);
    setIdentityRecoveryError(null);
    try {
      await confirmIdentity();
    } catch {
      setIdentityRecoveryError("We still couldn't confirm your account. Try again.");
    } finally {
      identityPending.current = false;
      setIsCheckingIdentity(false);
    }
  };

  const switchIdentity = async (path: string, expected: SwitchExpectation) => {
    // An overlapping switch must not resolve as success: the caller would navigate away
    // and drop open work while the first switch is still unconfirmed.
    if (identityPending.current || logoutPending.current || heldIdentityRef.current) {
      throw new IdentityRecoveryError("An account check is already in progress.");
    }
    identityPending.current = true;
    const held = { user: queryClient.getQueryData<AuthUser | null>(["/api/auth/me"]) ?? null };
    heldIdentityRef.current = held;
    setShopFloorReplayHold(queryClient, true); // Synchronous: a focus/online event can fire before the effect below runs.
    setHeldIdentity(held);
    setIsCheckingIdentity(true);
    setIdentityRecoveryError(null);
    let switchError: unknown;
    let confirmed: AuthUser | null = null;
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), 15_000);
    try {
      await queryClient.cancelQueries({ queryKey: ["/api/auth/me"], exact: true });
      await apiRequest("POST", path, undefined, { signal: controller.signal });
    } catch (error) {
      switchError = error;
    } finally {
      clearTimeout(deadline);
    }
    try {
      // Even a successful POST is not a replacement for a complete current
      // identity. A failed POST may also have committed the switch.
      confirmed = await confirmIdentity();
    } catch {
      const recoveryError = new IdentityRecoveryError("We couldn't confirm your account. Try again or sign out.");
      setIdentityRecoveryError(recoveryError.message);
      // Recovery owns this feedback. Keep the switch rejected so callers cannot
      // navigate away, but do not mount a hidden toast above the retained dialog.
      throw recoveryError;
    } finally {
      identityPending.current = false;
      setIsCheckingIdentity(false);
    }
    // The switch succeeded only when the confirmed account is the one it asked for, whatever
    // happened to the POST response: a lost or failed response does not undo a committed switch,
    // and a 200 does not prove the session still holds it (another tab can sign in between the
    // POST and this check). Any other account is reported so the user is warned and stays put.
    if (!switchReachedExpected(expected, confirmed)) throw switchError ?? sessionChangedDuringSwitch();
  };

  const startImpersonating = (userId: string) => switchIdentity(`/api/admin/impersonate/${userId}`, { kind: "start", targetUserId: userId });
  const stopImpersonating = async () => {
    try {
      await switchIdentity("/api/admin/stop-impersonating", { kind: "stop" });
    } catch (error) {
      if (error instanceof IdentityRecoveryError) return;
      toast({ title: "Could not return to your account", description: "Confirm your account or try again.", variant: "destructive", severity: "error" });
    }
  };

  const resolvedUser = heldIdentity ? heldIdentity.user : user ?? null;
  const displayName = resolvedUser?.displayName || "";
  const email = resolvedUser?.email || "";
  const role = resolvedUser?.role ?? null;
  const isAdmin = role === "admin";
  const isManager = role === "manager";
  const specialties = resolvedUser?.specialties || [];
  const managedSpecialties = resolvedUser?.managedSpecialties || [];
  const allSpecialties = Array.from(new Set([...specialties, ...managedSpecialties]));
  const isAuthenticated = !!resolvedUser;
  const isRoleLoaded = heldIdentity ? !!heldIdentity.user : isSuccess;
  const isSuperAdmin = resolvedUser?.isSuperAdmin ?? false;
  const isImpersonating = resolvedUser?.impersonating ?? false;
  const originalDisplayName = resolvedUser?.originalDisplayName ?? "";

  const isReadOnly = !isAdmin && !isManager &&
    specialties.length > 0 &&
    specialties.every(s => (READ_ONLY_SPECIALTIES as readonly string[]).includes(s));

  const hasSpecialty = useCallback(
    (s: string) => isAdmin || isManager || specialties.includes(s),
    [isAdmin, isManager, specialties],
  );

  const hasAnySpecialty = useCallback(
    (ss: string[]) => isAdmin || isManager || ss.some(s => specialties.includes(s)),
    [isAdmin, isManager, specialties],
  );

  return (
    <UserContext.Provider
      value={{
        user: resolvedUser,
        displayName,
        email,
        isSet: !!displayName.trim(),
        isAuthenticated,
        isLoading: isIdentityLoading({ identityHeld: !!heldIdentity, queryLoading: isLoading, cacheRestoring }),
        role,
        isAdmin,
        isManager,
        specialties,
        managedSpecialties,
        allSpecialties,
        isRoleLoaded,
        isReadOnly,
        isSuperAdmin,
        isImpersonating,
        originalDisplayName,
        hasSpecialty,
        hasAnySpecialty,
        logout,
        isLoggingOut,
        stopImpersonating,
        startImpersonating,
        isIdentityUnconfirmed: !!heldIdentity,
        isCheckingIdentity,
        identityRecoveryError,
        retryIdentity,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
